/**
 * AppPageHeader — Header padronizado de página
 *
 * Substitui os headers manuais escritos em cada tela.
 * Desktop: 56px | Mobile: 48px
 *
 * Uso:
 *   <AppPageHeader
 *     title="Playlists"
 *     count={playlists.length}
 *     helpContent={<Helper />}
 *     actions={<Button onClick={handleAdd}>Nova Playlist</Button>}
 *     mobileActions={<IconButton onClick={handleAdd}><Add /></IconButton>}
 *   />
 */

import { HelpOutline } from '@mui/icons-material'
import { Box, Chip, IconButton, Modal, Tooltip, Typography, useMediaQuery, useTheme } from '@mui/material'
import { ReactNode, useState } from 'react'
import { ds } from '../../theme/ds'
import ModalHeader from '../Modal/ModalHeader'

type Props = {
  title: string
  count?: number
  /** Ações exibidas no desktop (botões completos) */
  actions?: ReactNode
  /** Ações exibidas no mobile (ícones compactos, 36×36px) */
  mobileActions?: ReactNode
  /** Conteúdo do modal de ajuda. Se omitido, ícone de ajuda não aparece. */
  helpContent?: ReactNode
}

export default function AppPageHeader({ title, count, actions, mobileActions, helpContent }: Props) {
  const theme    = useTheme()
  const isDark   = theme.palette.mode === 'dark'
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const [helpOpen, setHelpOpen] = useState(false)

  const teal = isDark ? '#14b8a0' : '#0d9488'
  const bdr  = isDark ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.07)'

  return (
    <>
      <Box sx={{
        px: isMobile ? ds.spacing.pagePaddingMobile : ds.spacing.pagePaddingDesktop,
        py: isMobile ? 1.25 : 1.5,
        borderBottom: `1px solid ${bdr}`,
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        minHeight: isMobile ? ds.height.headerMobile : ds.height.headerDesktop,
        flexShrink: 0,
        bgcolor: 'background.default',
      }}>

        {/* Título + contador */}
        <Typography sx={{
          fontSize: isMobile ? ds.font.pageTitleMobile : 20,
          fontWeight: ds.font.bold,
          color: 'text.primary',
          letterSpacing: '-0.3px',
          lineHeight: 1.2,
        }}>
          {title}
        </Typography>

        {count !== undefined && (
          <Chip
            label={count}
            size="small"
            sx={{
              height: ds.height.btnXs,
              fontSize: ds.font.badge,
              fontWeight: ds.font.semibold,
              bgcolor: isDark ? 'rgba(20,184,160,0.15)' : 'rgba(13,148,136,0.1)',
              color: teal,
              border: 'none',
              '& .MuiChip-label': { px: '7px' },
            }}
          />
        )}

        <Box sx={{ flex: 1 }} />

        {/* Ações */}
        {isMobile ? mobileActions : actions}

        {/* Ajuda */}
        {helpContent && (
          <Tooltip title="Ajuda">
            <IconButton
              size="small"
              onClick={() => setHelpOpen(true)}
              sx={{ width: 36, height: 36, color: 'text.disabled', flexShrink: 0 }}
            >
              <HelpOutline sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {/* Modal de ajuda */}
      {helpContent && (
        <Modal open={helpOpen} onClose={() => setHelpOpen(false)}>
          <Box sx={{
            position: 'absolute', top: '50%', left: '50%',
            transform: 'translate(-50%,-50%)',
            bgcolor: 'background.paper',
            borderRadius: ds.radius.card,
            p: 4,
            maxWidth: 560, width: '90%', maxHeight: '85vh', overflowY: 'auto',
          }}>
            <ModalHeader title={title} onClose={() => setHelpOpen(false)} />
            {helpContent}
          </Box>
        </Modal>
      )}
    </>
  )
}
